// The i18next singleton, not `src/i18n`. `src/i18n` is the bootstrap that
// calls `init` on this same instance, and `helpers/toast` is imported by ~240
// modules; pulling the bootstrap in behind it would drag i18next's react
// plugin and every language file into module graphs that never had them, and
// break any test that partially mocks react-i18next.
import i18next from 'i18next';
import { CodeError, LocalizedError, StatusError } from './customError';

/** Copy for a failure we cannot explain any more precisely. */
export const UNKNOWN_ERROR_KEY = 'error.common.errorUnknown';

/** Copy for a 5xx, where the body is an implementation detail either way. */
const SERVER_ERROR_KEY = 'error.common.internal';

/**
 * Whether the thrower already localized this error's message.
 *
 * `LocalizedError` says so explicitly. `CodeError` predates it and every one
 * of its construction sites passes `i18n.t(...)`, so it counts too.
 *
 * @param error - The value a `catch` block received.
 * @returns True when `error.message` is copy we may render as-is.
 */
function isAlreadyLocalized(error: unknown): error is Error {
	return error instanceof LocalizedError || error instanceof CodeError;
}

/**
 * Resolves a caught value into localized copy safe to show a user.
 *
 * Never returns raw backend text. The original message is not lost:
 * `helpers/api` has already reported it to Sentry through `trackOperation` by
 * the time we get here.
 *
 * Mapping a specific backend failure to specific copy is deliberately not done
 * here. `services/issues/savedFilterError` is the pattern for that: it lives
 * beside the feature whose API it knows, and keys on the API's stable error
 * code rather than its English prose. A caller with copy of its own passes
 * `fallbackKey`; everything else lands on the generic message.
 *
 * @param error - The value a `catch` block received.
 * @param fallbackKey - Translation key for a failure we cannot classify.
 *                      Pass a feature-specific key where one exists.
 * @returns Localized copy safe to hand to `toast.error`.
 */
export function resolveErrorMessage(error: unknown, fallbackKey: string = UNKNOWN_ERROR_KEY): string {
	if (isAlreadyLocalized(error)) {
		return error.message;
	}

	if (error instanceof StatusError && error.status >= 500) {
		return i18next.t(SERVER_ERROR_KEY);
	}

	return i18next.t(fallbackKey);
}
